import { Options } from 'highcharts';
import React from 'react';
// eslint-disable-next-line import/no-internal-modules
import { renderToStaticMarkup } from 'react-dom/server';
import { ProjectFinancialsContractType } from 'api/project';
import { currencyRenderer } from 'components/atoms/currency-renderer';
import { formatterWithMinutes } from 'components/atoms/input-duration/formatter';
import {
  ChartLegendIndicator,
  GaugeChartLabelContainer,
  GaugeChartValue,
  StatLabelWrapper,
  StatValueWrapper,
} from '../../components/common';
import {
  ProjectFinancialsGaugeChartProps,
  ProjectFinancialsComponentGroup,
  ProjectFinancialsGaugeValueRange,
  ProjectFinancialsBaseChartProps,
} from '../../types';
import {
  projectFinancialsBaseChartOptions,
  ProjectFinancialsGaugeChartColorMap,
} from './common';

interface ProjectFinancialsGaugeChartMeta
  extends ProjectFinancialsBaseChartProps {
  financialContractType: ProjectFinancialsContractType;
}

export const projectFinancialsGaugeChartOptions =
  ({
    financialContractType,
    currencyCode,
    revenueLabels,
    timeLabels,
    percentageLabels,
  }: ProjectFinancialsGaugeChartMeta) =>
  ({
    type,
    value,
    max,
    remainingValue,
    valueLabel,
    maxLabel,
    remainingLabel,
  }: ProjectFinancialsGaugeChartProps): Options => ({
    ...projectFinancialsBaseChartOptions({
      chartOptions: {
        type: 'solidgauge',
        height: '260px',
      },
      tooltipProps: {
        type: 'solidgauge',
        showX: true,
        currencyCode,
        revenueLabels,
        timeLabels,
        percentageLabels,
        maxValue: max,
        maxLabel,
        remainingValue,
        remainingLabel,
      },
    }),
    pane: {
      center: ['50%', '75%'],
      size: '150%',
      startAngle: -90,
      endAngle: 90,
      background: [
        {
          backgroundColor: 'var(--gray-10)',
          innerRadius: '60%',
          outerRadius: '100%',
          shape: 'arc',
          borderWidth: 0,
        },
      ],
    },
    yAxis: {
      lineWidth: 0,
      tickWidth: 0,
      minColor:
        ProjectFinancialsGaugeChartColorMap[financialContractType][type][
          value < max
            ? ProjectFinancialsGaugeValueRange.InRange
            : ProjectFinancialsGaugeValueRange.OutOfRange
        ],
      maxColor:
        ProjectFinancialsGaugeChartColorMap[financialContractType][type][
          value < max
            ? ProjectFinancialsGaugeValueRange.InRange
            : ProjectFinancialsGaugeValueRange.OutOfRange
        ],
      tickInterval: 1,
      minTickInterval: 1,
      minorTickLength: 0,
      labels: {
        y: 25,
        distance: -30,
        overflow: 'allow',
        useHTML: true,
        formatter: (obj) => {
          if (obj.isFirst) {
            return renderToStaticMarkup(
              <GaugeChartLabelContainer>
                <StatValueWrapper>
                  {type === ProjectFinancialsComponentGroup.Budget
                    ? currencyRenderer(0, currencyCode)
                    : formatterWithMinutes(0)}
                </StatValueWrapper>
              </GaugeChartLabelContainer>,
            );
          }

          if (obj.isLast) {
            return renderToStaticMarkup(
              <GaugeChartLabelContainer>
                <StatValueWrapper>
                  {type === ProjectFinancialsComponentGroup.Budget
                    ? currencyRenderer(max, currencyCode)
                    : formatterWithMinutes(max)}
                </StatValueWrapper>
              </GaugeChartLabelContainer>,
            );
          }

          return '';
        },
      },
      min: 0,
      max,
    },
    series: [
      {
        name: valueLabel,
        type: 'solidgauge',
        data: [value],
        dataLabels: {
          y: -25,
          borderWidth: 0,
          useHTML: true,
          formatter: () => {
            return renderToStaticMarkup(
              <GaugeChartLabelContainer>
                <StatLabelWrapper>
                  <ChartLegendIndicator
                    color={
                      ProjectFinancialsGaugeChartColorMap[
                        financialContractType
                      ][type][
                        value < max
                          ? ProjectFinancialsGaugeValueRange.InRange
                          : ProjectFinancialsGaugeValueRange.OutOfRange
                      ]
                    }
                    shape="circle"
                  />
                  {valueLabel}
                </StatLabelWrapper>
                <GaugeChartValue>
                  {type === ProjectFinancialsComponentGroup.Budget
                    ? currencyRenderer(value, currencyCode)
                    : formatterWithMinutes(value)}
                </GaugeChartValue>
              </GaugeChartLabelContainer>,
            );
          },
        },
      },
    ],
  });
